import { Fragment, useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import {
  getConfirmPayment,
  getTerminatePayment,
  initiatePayment,
  numberWithCommas,
  accountLookUp,
  banksWithAccountNumber,
} from "../api/service";
import { usePopup } from "../context";
import { Store } from "react-notifications-component";
import copy from "../assets/copy.svg";
import editIcon from "../assets/edit.svg";
import {
  useCalculatedAmount,
  useSentMoney,
  useSocket,
} from "../hooks";
import { ErrorWrapper, GenericRefErrorWrapper, StandardAppWidth } from "./ussd";
import { Timer } from "./timer";
import { ChargesSection } from "./charge";
import { TwoText } from "./components/two-text";
import { AppButton } from "./components/app-button";
import { Spacer } from "./components/spacer";
import { FailedTransaction } from "./views/failed-transaction";
import { SuccessfulTransaction } from "./views/sucessful-transaction";
import { SelectBankSection } from "./views/select-bank";
import { ProcessingTransaction } from "./views/processing-transaction";
import { ReversedTransaction } from "./views/reversed-transaction";
import { ExpiredBankTransaction } from "./views/expired-bank-transaction";
import { EventName, postEvent } from "../dropin/events";
import { BankTextProps } from "../dropin/types";
import { CountDownExpiration } from "./views/count-down-expiration";
import { FormControl, InputLabel, MenuItem } from "@mui/material";
import Select from "@mui/material/Select";
import { Spinner } from "./spinner";

export const BankSection = () => {
  const [isError, setErrorState] = useState(false);
  const [intervalId, setIntervalId] = useState<any>(null);
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);

  const {
    merchantDetails: { account_name, account_number, bank_name, ...others },
    setMerchantDetails,
    options,
    pinValue,
    isSuccess,
    setIsTimeLapsed,
    isTimeLapsed,
    setIsBankSet,
    setIsConfirmClicked,
    showCharge,
    isBankSet,
    selectedBank,
    isFailed,
    isRefUsed,
    isConfirmClicked,
    isReversal,
    setIsSuccess,
    setPaymentPayload,
    customerName,
    setIsTransactionStarted,
    setPaymentReference,
    paymentReference,
    isCountDownExpired,
    setIsCountDownExpired,
  } = usePopup();

  const { loading, onClick } = useSentMoney();
  const { AmountToShow, calculatedCharge } = useCalculatedAmount();

  const onBankButtonClick = () => {
    setIsConfirmClicked!(true);
    onClick();
  };

  useSocket("bank");
  const {
    txn_charge,
    txn_charge_type,
    qrid = "",
    pay_category = "",
    amount,
    onSuccess
  } = options;

  let info = {
    live_mode: false,
    amount: AmountToShow,
    // customer_name: options.name,
    customer_name: customerName,
    customer_account_number: pinValue!.slice(-4),
    customer_email: options.email,
    customer_phone: options.customer_phone ?? "",
    transactionref: paymentReference === "" ? options.transactionref: paymentReference,
    payment_option: "bank",
    bankCode: selectedBank[0]?.code,
    original_amount: amount,
    full_customer_account_number: pinValue
  };

  let addedOptionArray = { pay_category, qrid };
  Object.keys(addedOptionArray).forEach((option) => {
    if (addedOptionArray[option] !== "") {
      info[option] = addedOptionArray[option];
    }
  });

  useEffect(() => {
    if (isSuccess || isTimeLapsed) {
      clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isTimeLapsed]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await initiatePayment(options.key, info);
        setPaymentReference!("");
        setMerchantDetails!(res.data.data);
        if (!res.data.data.account_number || !res.data.data.account_name) {
          setErrorState(true);
        }
        setIsBankSet!(true);
        let newintervalID = setInterval(async () => {
          try {
            const res = await getConfirmPayment(
              info.transactionref,
              options.key
            );
            setPaymentPayload!(res?.data?.data);
            if (res?.data?.data?.paymentstatus === "paid") {
              setIsSuccess!(true);
              setTimeout(() => {
                postEvent({
                  target: window.parent,
                  event: {
                    name: EventName.RETURN,
                    payload: {
                      message: `Transaction successful. Amount: ${AmountToShow}`,
                      code: "00",
                      payment_payload: res?.data,
                    },
                  },
                });
              }, 2000);
            }
          } catch (error: any) {
            console.error(error);
            Store.addNotification({
              insert: "top",
              container: "top-right",
              message: 'Error: "The transaction has not been confirmed"',
              type: "danger",
              dismiss: {
                duration: 3000,
                pauseOnHover: true,
              },
            });
            // setLoading(false);
          }
        }, 30000);
        setIntervalId(newintervalID);
      } catch (error: any) {
        console.log("Error:", error?.response?.data?.message);
        Store.addNotification({
          insert: "top",
          container: "top-right",
          message: error?.response?.data?.message ?? "An error occured.",
          type: "danger",
        });
      }
    };

    isBankSet &&
      !isSuccess &&
      (txn_charge && txn_charge_type ? !showCharge : true) &&
      fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBankSet, isSuccess]);

  const onTimeStopped = async () => {
    try {
      await getTerminatePayment(info.transactionref, options.key);
      setIsCountDownExpired!(true)
    } catch (error: any) {
      console.log(error);
    }
  };

  const changeOriginBank = ()=>{
    setIsBottomSheetOpen(true);
    clearInterval(intervalId);
  }

  const handleBottomSheetClose = () => {
    setIsBottomSheetOpen(false);
  }

  const handleBottomSheetSave = async () => {
    setIsBottomSheetOpen(false);
    clearInterval(intervalId);
    setIsBankSet!(false);
    setIsTransactionStarted!(false);
    setPaymentReference!(info.transactionref);
    setTimeout(() => {
      setIsBankSet!(true);
    }, 100);
  }

  const EditBankForm = ({ onSave, onCancel }: { onSave: () => void; onCancel: () => void }) => {
    const {
      banks,
      setSelectedBank,
      setPinValue,
      setIsBankSet,
      selectedBank: currentSelectedBank,
      pinValue: currentPinValue,
      setMerchantDetails,
      options: { txn_charge, txn_charge_type, key },
      setIsTransactionStarted,
      setCustomerName
    } = usePopup();

    const [localBank, setLocalBank] = useState(currentSelectedBank[0]?.code || "");
    const [localAccountNo, setLocalAccountNo] = useState(currentPinValue || "");
    const [lookupLoading, setLookupLoading] = useState(false);
    const [accountName, setAccountName] = useState("");
    const [accountDetailsContent, setAccountDetailsContent] = useState(false);

    const nubanLookup = useCallback(async () => {
      if (localAccountNo?.length === 10 && localBank) {
        const data = {
          account_number: localAccountNo,
          bank_code: localBank,
        };
        try {
          setLookupLoading(true);
          const res = await accountLookUp(key, data);
          setLookupLoading(false);
          if (res.data.accountname) {
            setAccountName(res?.data?.accountname);
          } else {
            Store.addNotification({
              insert: "top",
              container: "top-right",
              message: res?.data?.message,
              type: "danger",
              dismiss: { duration: 2000 },
            });
          }
        } catch (error) {
          setLookupLoading(false);
          Store.addNotification({
            insert: "top",
            container: "top-right",
            message: "Error fetching account name. Please try again.",
            type: "danger",
            dismiss: { duration: 2000 },
          });
        }
      }
    }, [localBank, localAccountNo, key]);

    useEffect(() => {
      if (localAccountNo?.length === 10 && localBank) {
        nubanLookup();
      }
    }, [nubanLookup, localAccountNo?.length]);

    const handleSave = () => {
      if (localBank && localAccountNo.length === 10 && accountDetailsContent) {
        const filteredBanks = banksWithAccountNumber(banks);
        const bank = filteredBanks.find((b: any) => b.code === localBank);
        if (bank) {
          // Update bank and account information
          setSelectedBank!([bank]);
          setPinValue!(localAccountNo);
          // Update customer name if available
          if (accountName) {
            setCustomerName!(accountName);
          }
          // Close modal and trigger re-initiation
          onSave();
        }
      } else {
        Store.addNotification({
          insert: "top",
          container: "top-right",
          message: "Please fill all fields and confirm the account details.",
          type: "danger",
          dismiss: { duration: 2000 },
        });
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if ((event.ctrlKey || event.metaKey) && event.key === "v") return;
      if (event.key === "Delete" || event.key === "Backspace") return;
      if (!/\d/.test(event.key)) {
        event.preventDefault();
      }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedValue = event.clipboardData.getData("text/plain").replace(/\D/g, "");
      const sanitizedValue = pastedValue.slice(0, 10);
      //@ts-ignore
      event.target.value = sanitizedValue;
      event.preventDefault();
      if (sanitizedValue.length === 10 && localBank) {
        setLocalAccountNo(sanitizedValue);
      }
    };

    return (
      <FormContainer>
        <Spacer height={10} />
        <StandardAppWidth>
          <FormControl fullWidth>
            <InputLabel id="bottom-sheet-bank-label" style={{ fontSize: "14px", color: "#B7B7B9", fontWeight: "400" }}>
              Select your bank
            </InputLabel>
            <Select
              sx={{
                boxShadow: "none",
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
                backgroundColor: "#F8F8FC",
                borderRadius: "4px",
                height: "52px",
              }}
              labelId="bottom-sheet-bank-label"
              id="bottom-sheet-bank-select"
              label="Select your bank"
              value={localBank}
              onChange={(e) => {
                const bankCode = e.target.value;
                const filteredBanks = banksWithAccountNumber(banks);
                const bank = filteredBanks.find((b: any) => b.code === bankCode);
                if (bank) {
                  setLocalBank(bankCode);
                }
              }}
            >
              {banks && banks.length > 0 ? (
                banksWithAccountNumber(banks).map((bank: { code: string; name: string }) => (
                  <MenuItem key={bank.code} value={bank.code}>
                    {bank.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>Loading banks...</MenuItem>
              )}
            </Select>
          </FormControl>
        </StandardAppWidth>
        <Spacer height={20} />
        {lookupLoading && <Spinner />}
        <StandardAppWidth>
          <EditInputField
            type="text"
            inputMode="numeric"
            onKeyDown={handleKeyDown}
            placeholder="Enter account number"
            maxLength={10}
            value={localAccountNo}
            onChange={(e) => setLocalAccountNo(e.target.value)}
            onPaste={handlePaste}
          />
        </StandardAppWidth>
        {accountName?.trim() !== "" && (
          <>
            <Spacer height={10} />
            <StandardAppWidth>
              <NameDisplay>{accountName}</NameDisplay>
            </StandardAppWidth>
            <Spacer height={20} />
            <StandardAppWidth>
              <div style={{ width: "100%", display: "flex", marginBottom: "15px", alignItems: "flex-start", justifyContent: "flex-start", columnGap: "3px", boxSizing: "border-box" }}>
                <input
                  type="checkbox"
                  checked={accountDetailsContent}
                  onChange={(e) => setAccountDetailsContent(e.target.checked)}
                  style={{ marginTop: "2px" }}
                />
                <BankText style={{ color: "#48484A", textAlign: "left", fontSize: "12px", fontWeight: 400, lineHeight: "16px" }}>
                  Please confirm this is the sender's account. It will be used for transaction verification.
                </BankText>
              </div>
            </StandardAppWidth>
          </>
        )}
        <Spacer height={20} />
        <StandardAppWidth>
          <ButtonContainer>
            <AppButton
              type="grayBtn"
              disabled={false}
              onClick={onCancel}
              isBusy={false}
              name="Cancel"
            />
            <AppButton
              type="redBtn"
              disabled={localAccountNo?.length !== 10 || !localBank || !accountDetailsContent}
              onClick={handleSave}
              isBusy={false}
              name="Update Bank"
            />
          </ButtonContainer>
        </StandardAppWidth>
      </FormContainer>
    );
  };

  const accountDetailsReady = Boolean(account_name && account_number && bank_name);

  const renderInfo = () => (
    <Fragment>
      {!isConfirmClicked ? (
        <BankSelectWrapper>
          {!isError && (
            <Fragment>
              {accountDetailsReady ? (
                <>
                  <BankText>
                    Transfer {options?.currency ?? "NGN"}{" "}
                    {numberWithCommas(AmountToShow)} to {account_name}
                  </BankText>
                  <Spacer height={25} />
                  <BankDetailsWrapper>
                    <TwoText
                      topText="ACCOUNT YOU ARE SENDING FROM"
                      bottomText={`${(selectedBank && selectedBank[0]?.name) || ""}${pinValue ? `\n${pinValue}` : ""}`}
                      icon={''}
                      isCurrency={false}
                      editIcon={editIcon}
                      onEditIconClick={() => changeOriginBank()}
                    />
                    <TwoText
                      topText="ACCOUNT YOU ARE SENDING TO"
                      bottomText={`${bank_name}\n${account_number}`}
                      icon={copy}
                      isCurrency={false}
                      copyValue={account_number}
                    />
                    <TwoText
                      topText="AMOUNT"
                      bottomText={JSON.stringify(AmountToShow)}
                      icon={copy}
                      isCurrency={true}
                    />
                    {(options?.display_charge ?? true) && (
                      <ChargeWrapper>Processing Fee {calculatedCharge}</ChargeWrapper>
                    )}
                  </BankDetailsWrapper>
                  <Spacer height={20} />
                </>
              ) : (
                <BankDetailsWrapper>
                  <SpinnerWrapper>
                    <Spinner />
                  </SpinnerWrapper>
                </BankDetailsWrapper>
              )}
              <BankText style={{ color: "red" }}>
                <StandardAppWidth >
                <PaymentAccountWarning>
                  <ul style={{paddingLeft:"10px"}}>
                    {/* <li>
                      <div>
                      The sender account details from the previous screen will be used to verify your payment. <span style={{fontWeight: "bold"}}>Picked the wrong bank?</span> {" "}
                        <span
                          style={{
                            color: "blue",
                            fontStyle: "italic",
                            cursor: "pointer",
                          }}
                          onClick={() => changeOriginBank()}
                        >
                          Change Bank.
                        </span>
                      </div>
                    </li> */}
                    <li>Send <span style={{fontWeight: "bold"}}>EXACT</span> amount as stated above, otherwise your payment will be reversed.</li>
                    <li>If your payment comes in <span style={{fontWeight: "bold"}}>AFTER</span> the countdown below has expired, it will be reversed.</li>
                  </ul>
                </PaymentAccountWarning>
                </StandardAppWidth>
          
              </BankText>

              <Spacer height={25} />
              <Timer onStop={() => onTimeStopped()} time={2} />
              <Spacer height={30} />
              <AppButton
                type="red"
                disabled={false}
                onClick={() => onBankButtonClick()}
                isBusy={loading}
                name="I've sent the money"
              />
            </Fragment>
          )}
          <ErrorWrapper isError={isError} type="Bank" />
        </BankSelectWrapper>
      ) : (
        <ProcessingTransaction />
      )}
    </Fragment>
  );

  const ErrorTxt = `Please check your account balance. If you have been debited kindly reach out to ${
    options.customer_service_channel || ""
  } and try again.`;

  const renderHelper = () => {
    if (isRefUsed) {
      return <GenericRefErrorWrapper />;
    }
    if (!isBankSet) {
      return <SelectBankSection />;
    } else {
      if (isSuccess) return <SuccessfulTransaction />;
      if(isReversal) return <ReversedTransaction />;
      if (isTimeLapsed) return <ExpiredBankTransaction />;
      if (isCountDownExpired) return <CountDownExpiration />;
      if (showCharge) return <ChargesSection payment_option="bank" />;
      if (isFailed) return <FailedTransaction errorMessage={ErrorTxt} />;
      return renderInfo();
    }
  };

  return (
    <BankContainer>
      {renderHelper()}
      {isBottomSheetOpen && (
        <BottomSheetOverlay onClick={handleBottomSheetClose}>
          <BottomSheetContent onClick={(e) => e.stopPropagation()}>
            <BottomSheetHeader>
              <BankText style={{ fontSize: "16px", fontWeight: 600 }}>Update Source Bank</BankText>
            </BottomSheetHeader>
            <BottomSheetBody>
              <EditBankForm 
                onSave={handleBottomSheetSave}
                onCancel={handleBottomSheetClose}
              />
            </BottomSheetBody>
          </BottomSheetContent>
        </BottomSheetOverlay>
      )}
    </BankContainer>
  );
};

export const BankContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;


export const BankText = styled.p<BankTextProps>`
  font-size: 13px;
  text-align: center;
  letter-spacing: 0.01em;
  color: #1c1c1e;
  font-weight: 500;
`;

export const BankErrorText = styled.p`
  font-size: 15px;
  display: flex;
  align-items: center;
  text-align: center;
  letter-spacing: 0.01em;
  color: #1c1c1e;
  padding: 0 10px;
`;

export const BankDetailsWrapper = styled.div`
  background: #f8f8fc;
  border-radius: 6px;
  box-sizing: border-box;
  min-width: 282px;
  // max-width: 400px;
  padding: 25px 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  height: auto;
  min-height: 260px;
  &:not(:last-of-type) {
    row-gap: 22px;
  }

  @media (max-width: 600px) {
    width: 355px !important;
  }

  @media (max-width: 370px) {
    width: 282px !important;
  }

  @media (max-width: 311px) {
    width: 252px !important;
  }
`;

export const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 150px;
`;

export const UnstyledButton = styled.button<BankTextProps>`
  background: none;
  cursor: pointer;
  border: 0;
  color: red;
  font-weight: 500;
`;

export const GreyButton = styled.button<BankTextProps>`
  background: #f8f8fc;
  cursor: pointer;
  border: 1px solid #ebebf0;
  border-radius: 4px;
  color: #636366;
  font-weight: 500;
`;

export const TextLabel = styled.p`
  text-align: center;
  // max-width: 57.5%;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  padding: 0 1px;
  letter-spacing: 0.01em;
  color: #1c1c1e;
  // line-height: 5px;
`;

export const WarningText = styled.div`
  text-align: center;
  font-weight: 400;
  font-size: 12px;
  color: red;
  border: 0.7px solid #fbb0b3;
  height: 38px;
  width: 100%;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  // line-height: 5px;

 
`;

export const BankSelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

export const ChargeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #e7f8f0;
  border-radius: 5px;
  padding: 3px 6px;
  // width: 40px;
  height: 13px;
  // column-gap: 10px;
  color: #12b76a;
  font-weight: 500;
  font-size: 12px;
  margin-top: -15px;
`;

export const TimerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid #ebebf0;
  padding: 0 20px;
  background: #ffffff;
  border-radius: 0px 5px 5px 0px;
  height: 38px;
`;

export const TimerText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 18px;
  color: #636366;
  letter-spacing: 0.01em;
`;

export const NameDisplay = styled.div`
box-sizing: border-box;
  // text-align: center;
  font-size: 10px;
  font-weight: 500;
  color: #ae207c;
  height: 40px;
  width: 100%;
  place-self: flex-start;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f7e9f2;
  padding: 0 5px;


`;

export const PaymentAccountWarning = styled.div`
box-sizing: border-box;
  text-align: left;
  font-size: 11px;
  font-weight: 400;
  color: #DD000A;
  // height: 26px;
  width: 100%;
  // place-self: flex-start;
  border-radius: 4px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  // background: #f7e9f2;
  padding-left: 15px;
  padding-right: 8px;
  border: 0.7px solid #FBB0B3;
  line-height: 18px;

`;

export const BottomSheetOverlay = styled.div<BankTextProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  animation: fadeIn 0.2s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const BottomSheetContent = styled.div<BankTextProps>`
  width: 100%;
  background: white;
  border-radius: 0;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease-out;
  
  @media (min-width: 600px) {
    max-width: 650px;
    margin: 0 auto;
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

export const BottomSheetHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ebebf0;
`;

export const CloseButton = styled.button<BankTextProps>`
  background: none;
  border: none;
  font-size: 32px;
  cursor: pointer;
  color: #1c1c1e;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    opacity: 0.7;
  }
`;

export const BottomSheetBody = styled.div`
  padding: 20px 0;
`;

export const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const EditInputField = styled.input<BankTextProps>`
  box-sizing: border-box;
  padding-left: 14px;
  font-size: 14px;
  background-color: #f8f8fc;
  color: #000000;
  border-color: #f8f8fc;
  border-radius: 4px;
  outline: none;
  width: 100%;
  height: 52px;
  border: none;
  border-width: 0px;
  border-style: 0;

  &:focus {
    border-color: #f8f8fc;
  }

  &::placeholder {
    color: #b7b7b9;
    margin-left: 40px;
  }
`;

export const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 12px;
  width: 100%;
  
  button {
    flex: 1;
  }
`;
